import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ActivationProvider } from "@/contexts/ActivationContext";
import { TimezoneProvider } from "@/lib/tz";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Trucks from "@/pages/Trucks";
import TruckDetail from "@/pages/TruckDetail";
import Trailers from "@/pages/Trailers";
import TrailerDetail from "@/pages/TrailerDetail";
import Repairs from "@/pages/Repairs";
import Services from "@/pages/Services";
import Alerts from "@/pages/Alerts";
import Drivers from "@/pages/Drivers";
import Reports from "@/pages/Reports";
import FuelPrices from "@/pages/FuelPrices";
import AIAssistant from "@/pages/AIAssistant";
import Users from "@/pages/Users";
import Inventory from "@/pages/Inventory";
import RepairShops from "@/pages/RepairShops";
import Integrations from "@/pages/Integrations";
import FleetMap from "@/pages/FleetMap";
import Jobs from "@/pages/Jobs";
import Settings from "@/pages/Settings";
import MarketingLanding from "@/pages/MarketingLanding";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import SuperAdminOverview from "@/pages/SuperAdminOverview";
import SuperAdminCompanies from "@/pages/SuperAdminCompanies";
import DriverPortal from "@/pages/DriverPortal";
import Messages from "@/pages/Messages";
import Geofences from "@/pages/Geofences";
import ComingSoon from "@/pages/ComingSoon";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import PublicTrack from "@/pages/PublicTrack";

// Public marketing landing when logged out.
// Once logged in, super_admin → /super-admin, mechanic → /jobs, else Dashboard.
function RootRoute() {
  const { user, checking } = useAuth();
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="font-mono text-sm text-slate-500" data-testid="auth-loading">INITIALIZING SESSION...</div>
      </div>
    );
  }
  if (!user) return <MarketingLanding />;
  if (user.role === "super_admin" && !user.is_impersonating) return <Navigate to="/super-admin" replace />;
  if (user.role === "driver") return <Navigate to="/driver" replace />;
  if (user.role === "mechanic") return <Navigate to="/jobs" replace />;
  return <Navigate to="/app" replace />;
}

function DashboardOrJobs() {
  const { user } = useAuth();
  if (user?.role === "mechanic") return <Navigate to="/jobs" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <AuthProvider>
      <ActivationProvider>
      <TimezoneProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* PUBLIC */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/billing/cancel" element={<BillingCancel />} />
          <Route path="/track/:token" element={<PublicTrack />} />
          <Route path="/track/:unit/:token" element={<PublicTrack />} />

          {/* SUPER ADMIN PORTAL */}
          <Route
            element={
              <ProtectedRoute superAdminOnly>
                <SuperAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/super-admin" element={<SuperAdminOverview />} />
            <Route path="/super-admin/companies" element={<SuperAdminCompanies />} />
          </Route>

          {/* DRIVER PORTAL — standalone (no company sidebar) */}
          <Route path="/driver" element={<ProtectedRoute><DriverPortal /></ProtectedRoute>} />

          {/* FLEET APP (Company Admin / Manager / Mechanic) */}
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/app" element={<DashboardOrJobs />} />
            <Route path="/trucks" element={<Trucks />} />
            <Route path="/trucks/:id" element={<TruckDetail />} />
            <Route path="/fleet-map" element={<FleetMap />} />
            <Route path="/trailers" element={<Trailers />} />
            <Route path="/trailers/:id" element={<TrailerDetail />} />
            <Route path="/repairs" element={<Repairs />} />
            <Route path="/services" element={<Services />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/drivers" element={<Drivers />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/fuel" element={<ComingSoon title="Fuel" eyebrow="Coming soon" />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/dispatch" element={<ComingSoon title="Dispatch" eyebrow="Coming soon" />} />
            <Route path="/geofences" element={<Geofences />} />
            <Route path="/repair-shops" element={<RepairShops />} />
            <Route path="/integrations" element={<ProtectedRoute adminOnly><Integrations /></ProtectedRoute>} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/fuel-prices" element={<FuelPrices />} />
            <Route path="/ai" element={<AIAssistant />} />
            <Route path="/users" element={<ProtectedRoute adminOnly><Users /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </TimezoneProvider>
      </ActivationProvider>
    </AuthProvider>
  );
}

export default App;
